import type { SuspensionNotice } from '../mission-specialist/suspensions/useMySuspensions'

import { useMutation, useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type NotificationsFilter = 'unread' | 'customer_comment' | 'mission_specialist_comment'
type NotificationsType =
  | 'comment_posted'
  | 'comment_reply'
  | 'comment_mention'
  | 'comment_liked'
  | 'shift_demotion'
  | 'shift_replaced'
  | 'shift_replacement_cancelled'
  | 'operator_shift_cancelled'
  | 'training_invitation'
  | 'training_invitation_reminder'
  | 'training_removed'
  | 'eligibility_suspended'
  | 'eligibility_enabled'
  | 'suspension_started'
  | 'suspension_ended'
  | 'suspension_requalified'
  | 'eligibility_removed'
  | 'training_reset'

type UserRole =
  | 'admin'
  | 'bot'
  | 'customer'
  | 'platform_user'
  | 'account_owner'
  | 'operator'
  | 'trainee'
  | 'applicant'

type TargetEntityType = 'customer_engagement' | 'task_resolution' | 'audit_task' | 'audit_engagement'

type NotificationsParams = {
  since?: Date
  filters?: NotificationsFilter[]
  cursor: number
  limit: number
}

type CommentOriginator = {
  id: string
  role: UserRole
  name: string
  avatarUrl?: string
}

type NotificationCommentContent = {
  content: string
  commentId: string
  targetEntityId: string
  targetEntityType: TargetEntityType
}

interface NotificationBase {
  id: string
  createdAt: Date
  isRead: boolean
  type: NotificationsType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>
}

interface NotificationShiftDemotion extends NotificationBase {
  type: 'shift_demotion'
  data: {
    user: {
      id: string
      name: string
      avatarUrl: string
    }
    originalShift: {
      providerId: string
      start: string
      end: string
    }
    reason?: 'NO_SHOW' | 'MID_SHIFT_DISCONNECTION' | 'NO_TASK_ASSIGNED_IN_PERIOD' | 'AFK_OR_OFFLINE'
    willBeReplaced?: boolean
    demotedShiftEndTime?: string
  }
}

type ShiftMeta = {
  providerId: string
  start: string
  end: string
}

type UserMeta = {
  id: string
  name: string
  avatarUrl: string
}

interface NotificationShiftReplaced extends NotificationBase {
  type: 'shift_replaced'
  data: {
    originalUser: {
      id: string
      name: string
      avatarUrl: string
    }
    replacedBy: {
      id: string
      name: string
      avatarUrl: string
    }
    originalShift: ShiftMeta
    replacementShift: ShiftMeta
    demotedShiftEndTime?: string
  }
}

interface NotificationShiftReplacementCancelled extends NotificationBase {
  type: 'shift_replacement_cancelled'
  data: {
    user: UserMeta
    originalShift: ShiftMeta
    demotedShiftEndTime?: string
  }
}

interface NotificationOperatorShiftCancelled extends NotificationBase {
  type: 'operator_shift_cancelled'
  data: {
    shift: ShiftMeta
    reason?: 'NO_SHOW' | 'MID_SHIFT_DISCONNECTION' | 'NO_TASK_ASSIGNED_IN_PERIOD' | 'AFK_OR_OFFLINE'
  }
}

type NotificationCommentData = {
  comment: NotificationCommentContent
  originator: CommentOriginator
}

interface NotificationComment extends NotificationBase {
  type: 'comment_posted' | 'comment_reply' | 'comment_mention' | 'comment_liked'
  data: NotificationCommentData
}

interface NotificationTrainingAndEligibility extends NotificationBase {
  type:
    | 'training_invitation'
    | 'training_invitation_reminder'
    | 'eligibility_suspended'
    | 'training_removed'
    | 'eligibility_enabled'
    | 'suspension_ended'
    | 'suspension_requalified'
    | 'eligibility_removed'
    | 'training_reset'
  data: {
    program: EligibilityNotificationProgram
  }
}

type EligibilityNotificationProgram = {
  id: string
  displayName: string
  color: string
  iconType?: string
}

export interface SuspensionStartedNotification extends NotificationBase {
  type: 'suspension_started'
  data: { program: EligibilityNotificationProgram } & Partial<SuspensionNotice>
}

type Notification =
  | NotificationShiftDemotion
  | NotificationShiftReplaced
  | NotificationShiftReplacementCancelled
  | NotificationOperatorShiftCancelled
  | NotificationComment
  | NotificationTrainingAndEligibility
  | SuspensionStartedNotification

type NotificationResponse = {
  total: number
  totalUnread: number
  notifications: Notification[]
}

type NotificationFiltersResponse = {
  count: number
  filter: NotificationsFilter
}

export function useGetNotifications(
  params: NotificationsParams,
  disabled?: boolean,
  supportedNotificationsFilter?: Notification['type'][]
) {
  return useQuery<NotificationResponse>({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/notifications', { params })
      return data
    },
    select: (data) => {
      if (supportedNotificationsFilter) {
        const filteredInNotifications = data.notifications.filter((notification) =>
          supportedNotificationsFilter.includes(notification.type)
        )

        return {
          ...data,
          total: filteredInNotifications.length,
          totalUnread: filteredInNotifications.filter((notification) => !notification.isRead).length,
          notifications: filteredInNotifications,
        } satisfies NotificationResponse
      }
      return data
    },
    enabled: !disabled,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useGetNotificationFilters() {
  return useQuery<NotificationFiltersResponse[]>({
    queryKey: ['notification-filters'],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/notifications/filters')
      return data
    },
  })
}

export function useMarkNotificationAsRead() {
  return useMutation({
    mutationFn: async (notificationId: string) => {
      return await apiClient.put(`/int/v3/platform/notifications/${notificationId}/read`)
    },
  })
}

export function useMarkAllNotificationsAsRead() {
  return useMutation({
    mutationFn: async () => {
      return await apiClient.put(`/int/v3/platform/notifications/read`)
    },
  })
}
